'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from './StoreContext';
import { getBusinessDate } from '../utils/date';

// v1→: ローカル伝票（TicketContextの明細/プラン/開始時刻）をブラウザ永続化しない
// 目的: logme_session_v1 由来の不整合を減らし、サーバ（DB+Redis）を正に統一する
const ENABLE_TICKET_CONTEXT_PERSISTENCE = false;

const resolveSessionBusinessDate = (): string => {
  if (typeof window === 'undefined') return getBusinessDate(new Date());
  try {
    const raw = window.localStorage.getItem('logme_session_v1');
    if (raw) {
      const root = JSON.parse(raw) as any;
      if (typeof root?.currentBusinessDate === 'string' && root.currentBusinessDate) {
        return root.currentBusinessDate;
      }
    }
  } catch {
    // ignore
  }
  return getBusinessDate(new Date());
};

export interface TicketLine {
  itemId: string;
  name: string;
  unitPrice: number;
  qty: number;
  /** 飲み放題プランに含めるドリンクかどうか */
  isAllFreeDrink?: boolean;
}

export interface TicketPlan {
  type: 'time' | 'bottle' | 'all_you_can_drink';
  startTime: string;
  options?: {
    time?: { duration: number };
    bottle?: { bottleType: string; bottlePrice: number; drinkBackRate: number };
    allYouCanDrink?: { duration: number; premiumDrinks: boolean };
  };
}

export interface TicketState {
  // tableId -> lines
  tickets: Record<string, TicketLine[]>;
  // tableId -> plan
  ticketPlans: Record<string, TicketPlan>;
  // tableId -> actual start time (when serving started)
  actualStartTimes: Record<string, string>;
  // フロア関連データ
  floorTableIds: string[];
  addItem: (tableId: string, line: Omit<TicketLine, 'qty'>, qty?: number) => void;
  setQty: (tableId: string, itemId: string, qty: number) => void;
  removeItem: (tableId: string, itemId: string) => void;
  clearTable: (tableId: string) => void;
  getLines: (tableId: string) => TicketLine[];
  getSubtotal: (tableId: string) => number;
  // オーダー編集・削除機能
  updateItem: (tableId: string, itemId: string, updates: Partial<Omit<TicketLine, 'itemId'>>) => void;
  deleteTicket: (tableId: string) => void;
  // プラン管理機能
  setTicketPlan: (tableId: string, plan: TicketPlan) => void;
  getTicketPlan: (tableId: string) => TicketPlan | undefined;
  // プランに応じた料金計算
  getDisplayPrice: (tableId: string, itemId: string, unitPrice: number) => number;
  // 実際の開始時間管理機能
  setActualStartTime: (tableId: string, startTime: string) => void;
  getActualStartTime: (tableId: string) => string | undefined;
  // フロア管理機能
  setFloorTableIds: (ids: string[]) => void;
  getFloorTableIds: () => string[];
}

const TicketContext = createContext<TicketState | undefined>(undefined);

export function TicketProvider({ children }: { children: React.ReactNode }) {
  const { selectedStore } = useStore();
  const [tickets, setTickets] = useState<Record<string, TicketLine[]>>({});

  const [ticketPlans, setTicketPlans] = useState<Record<string, TicketPlan>>({});
  const [actualStartTimes, setActualStartTimes] = useState<Record<string, string>>({});
  
  // フロア関連データのstate
  const [floorTableIds, setFloorTableIds] = useState<string[]>([]);

  // selectedStoreが変更されたらローカル状態をリセット（サーバ正）
  useEffect(() => {
    setTickets({});
    setTicketPlans({});
    setActualStartTimes({});
    setFloorTableIds([]);
  }, [selectedStore?.id]);

  // 旧フラットキーは「移行せず削除」する（後方互換なし）
  useEffect(() => {
    if (typeof window === 'undefined' || !selectedStore?.id) return;
    try {
      const prefixes = ['ticketStartAt:', 'ticketGroup:', 'ticketVisitId:', 'ticketCustomer:'];
      const keys = Object.keys(window.localStorage);
      keys.forEach((k) => {
        if (prefixes.some((p) => k.startsWith(p))) {
          window.localStorage.removeItem(k);
        }
      });
    } catch {
      // ignore
    }
  }, [selectedStore?.id]);

  // NOTE:
  // 以前は営業日切替時に localStorage を掃除していたが、
  // TicketContext自体の永続化を停止したため不要（他用途のlogme_session_v1を壊しうるため撤去）。

  // 個別キーへの書き込みは削除（logme_session_v1のみに統合）

  // NOTE: ローカル伝票の永続化（logme_session_v1へのtickets保存/ticketRuntime更新）は停止

  const addItem = useCallback((tableId: string, line: Omit<TicketLine, 'qty'>, qty: number = 1) => {
    if (!tableId) return;
    setTickets((prev) => {
      const current = prev[tableId] || [];
      const found = current.find((l) => l.itemId === line.itemId);
      let next: TicketLine[];
      if (found) {
        next = current.map((l) => (l.itemId === line.itemId ? { ...l, qty: l.qty + qty } : l));
      } else {
        next = [...current, { ...line, qty }];
      }
      return { ...prev, [tableId]: next };
    });
  }, []);

  const setQty = useCallback((tableId: string, itemId: string, qty: number) => {
    if (!tableId) return;
    setTickets((prev) => {
      const current = prev[tableId] || [];
      const next = current
        .map((l) => (l.itemId === itemId ? { ...l, qty: Math.max(0, Math.floor(qty)) } : l))
        .filter((l) => l.qty > 0);
      return { ...prev, [tableId]: next };
    });
  }, []);

  const removeItem = useCallback((tableId: string, itemId: string) => {
    if (!tableId) return;
    setTickets((prev) => {
      const current = prev[tableId] || [];
      const next = current.filter((l) => l.itemId !== itemId);
      return { ...prev, [tableId]: next };
    });
  }, []);

  const clearTable = useCallback((tableId: string) => {
    if (!tableId) return;
    setTickets((prev) => ({ ...prev, [tableId]: [] }));
  }, []);

  const getLines = useCallback((tableId: string) => {
    return (tickets[tableId] || []).slice();
  }, [tickets]);

  const getSubtotal = useCallback((tableId: string) => {
    const lines = tickets[tableId] || [];
    return lines.reduce((sum, l) => sum + l.unitPrice * l.qty, 0);
  }, [tickets]);

  const updateItem = useCallback((tableId: string, itemId: string, updates: Partial<Omit<TicketLine, 'itemId'>>) => {
    if (!tableId) return;
    setTickets((prev) => {
      const current = prev[tableId] || [];
      const next = current.map((l) =>
        l.itemId === itemId ? { ...l, ...updates } : l
      );
      return { ...prev, [tableId]: next };
    });
  }, []);

  const deleteTicket = useCallback((tableId: string) => {
    if (!tableId) return;
    setTickets((prev) => {
      const next = { ...prev };
      delete next[tableId];
      return next;
    });
    setTicketPlans((prev) => {
      const next = { ...prev };
      delete next[tableId];
      return next;
    });
    setActualStartTimes((prev) => {
      const next = { ...prev };
      delete next[tableId];
      return next;
    });
  }, []);

  // プラン管理機能
  const setTicketPlan = useCallback((tableId: string, plan: TicketPlan) => {
    if (!tableId) return;
    setTicketPlans((prev) => ({ ...prev, [tableId]: plan }));
  }, []);

  const getTicketPlan = useCallback((tableId: string) => {
    return ticketPlans[tableId];
  }, [ticketPlans]);

  // 実際の開始時間管理機能
  const setActualStartTime = useCallback((tableId: string, startTime: string) => {
    if (!tableId) return;
    setActualStartTimes((prev) => ({ ...prev, [tableId]: startTime }));
  }, []);

  const getActualStartTime = useCallback((tableId: string) => {
    return actualStartTimes[tableId];
  }, [actualStartTimes]);

  // フロア管理機能
  const setFloorTableIdsHandler = useCallback((ids: string[]) => {
    setFloorTableIds(ids);
  }, []);

  const getFloorTableIdsHandler = useCallback(() => {
    return floorTableIds.slice();
  }, [floorTableIds]);

  // プランに応じた料金計算
  const getDisplayPrice = useCallback(
    (tableId: string, itemId: string, unitPrice: number) => {
      const plan = ticketPlans[tableId];
      if (!plan) return unitPrice;

      const line = (tickets[tableId] || []).find((l) => l.itemId === itemId);

      // 時間制 or 飲み放題プランの場合:
      // フラグが立っているドリンクは0円、それ以外は通常料金
      if (plan.type === 'time' || plan.type === 'all_you_can_drink') {
        if (line?.isAllFreeDrink) {
          return 0;
        }
        return unitPrice;
      }

      // ボトル制プランの場合（現状は従来どおりそのまま表示）
      if (plan.type === 'bottle') {
        return unitPrice;
      }

      return unitPrice;
    },
    [ticketPlans, tickets],
  );

  const value: TicketState = useMemo(() => ({
    tickets, ticketPlans, actualStartTimes, floorTableIds,
    addItem, setQty, removeItem, clearTable, getLines, getSubtotal,
    updateItem, deleteTicket, setTicketPlan, getTicketPlan, getDisplayPrice, setActualStartTime, getActualStartTime,
    setFloorTableIds: setFloorTableIdsHandler, getFloorTableIds: getFloorTableIdsHandler
  }), [tickets, ticketPlans, actualStartTimes, floorTableIds, addItem, setQty, removeItem, clearTable, getLines, getSubtotal, updateItem, deleteTicket, setTicketPlan, getTicketPlan, getDisplayPrice, setActualStartTime, getActualStartTime, setFloorTableIdsHandler, getFloorTableIdsHandler]);

  return <TicketContext.Provider value={value}>{children}</TicketContext.Provider>;
}

export function useTicket() {
  const ctx = useContext(TicketContext);
  if (!ctx) throw new Error('useTicket must be used within TicketProvider');
  return ctx;
}


